import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  ShieldCheck, CheckCircle2, UserCheck, RotateCcw, ChevronDown, ExternalLink, Download,
} from 'lucide-react';
import ReadinessHtmlBlocks from './ReadinessHtmlBlocks';
import ReadinessRing from './ReadinessRing';

// Rollup categories in display order, with human labels. Mirrors
// ReadinessAssessmentResult category keys.
const CATEGORIES = [
  { key: 'valuation', label: 'Valuation' },
  { key: 'catastrophic_plan', label: 'Catastrophic Planning' },
  { key: 'partnering', label: 'Partnering & Succession' },
  { key: 'deal_structure', label: 'Deal Structure' },
  { key: 'communication', label: 'Client Communication' },
  { key: 'emotional_readiness', label: 'Emotional Readiness' },
];

// Three states, separated by colour AND icon rather than colour alone.
// `reviewed` is deliberately the quiet one: it means the advisor attested to the
// area, where `done` means the platform verified it. Keeping that legible
// matters more to CS and enterprise readers than to the advisor, which is why it
// reads as muted-resolved rather than as a third success colour.
const STATUS_META = {
  to_do: { label: 'To do', tone: 'text-succession-warning' },
  done: { label: 'Done', tone: 'text-succession-success' },
  reviewed: { label: 'Reviewed', tone: 'text-succession-text-secondary' },
};

const track = (name, properties) => {
  try {
    if (window.ahoy) window.ahoy.track(name, properties);
  } catch (_) { /* ahoy optional */ }
};

const isExternal = (url) => /^https?:\/\//i.test(url || '');

/**
 * ReadinessScoreCard
 *
 * Persistent home for a completed succession readiness result on the LaunchPad.
 * Shows the overall readiness score as a radial gauge plus a per-category gap
 * breakdown, and links down to the personalized QuickStart steps.
 *
 * Each category can carry "further reading" — the supporting RJ documents the
 * legacy Paddy flow linked from its post-answer copy, resolved server-side and
 * scoped to the questions this advisor was actually asked. Categories RJ has no
 * material for simply render without a link; the "all resources" panel at the
 * foot is the catch-all, and is the only thing an advisor with no gaps at all
 * would otherwise see.
 *
 * Props:
 * - result: ReadinessAssessmentResult ({ categoryScores, categoryStatuses,
 *           overallScore, completedAt, furtherReading, allResourcesHtml })
 * - onToggleReview: (category, reviewed) => void. Omit to render the breakdown
 *           read-only — the per-row control disappears with it.
 * - reviewingCategory: category key currently in flight, for disabling its control
 * - className
 */
const ReadinessScoreCard = ({
  result,
  onRetake,
  retaking = false,
  onToggleReview,
  reviewingCategory = null,
  downloadUrl,
  className = '',
}) => {
  // Single-open accordion: this is a compact LaunchPad tile, and letting every
  // category expand at once pushes the QuickStart guide off the fold.
  const [openCategory, setOpenCategory] = useState(null);
  const [showAllResources, setShowAllResources] = useState(false);

  if (!result || !result.categoryScores) return null;

  const overall = Math.round(result.overallScore || 0);
  const scores = result.categoryScores;

  const linksByCategory = (result.furtherReading || []).reduce((acc, group) => {
    if (group && group.category) acc[group.category] = group.links || [];
    return acc;
  }, {});

  // Status is resolved server-side (ReadinessAreaStatusService) because it reads
  // live signals the frozen categoryScores cannot express: an advisor who files
  // their catastrophic plan after answering "no" sits at 1/2 forever, and 1/2 is
  // a gap. Deriving it here from the score would reproduce exactly the bug this
  // replaced.
  const statusByCategory = (result.categoryStatuses || []).reduce((acc, entry) => {
    if (entry && entry.category) acc[entry.category] = entry;
    return acc;
  }, {});

  const rows = CATEGORIES.map(({ key, label }) => {
    const sm = scores[key] || {};
    const score = Number(sm.score) || 0;
    const max = Number(sm.max) || 0;
    const live = statusByCategory[key];
    // Fallback reproduces this card's behaviour from before categoryStatuses
    // existed, so a failed resolver degrades to a stale-but-populated breakdown
    // rather than an empty one.
    const fallbackStatus = (max > 0 && score < max) ? 'to_do' : 'done';
    const status = live ? live.status : fallbackStatus;
    const inScope = live ? live.inScope : max > 0;
    return { key, label, status, inScope, links: linksByCategory[key] || [] };
  }).filter((r) => r.inScope);

  const gapCount = rows.filter((r) => r.status === 'to_do').length;
  const statusLine = gapCount === 0
    ? "You're on track across every area."
    : `${gapCount} ${gapCount === 1 ? 'area needs' : 'areas need'} attention.`;


  // "Assessed in March" reads better than a full timestamp and gives the retake
  // link its reason for being there.
  const takenLabel = (() => {
    if (!result.completedAt) return null;
    const date = new Date(result.completedAt);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  })();

  const toggleCategory = (key) => {
    const next = openCategory === key ? null : key;
    setOpenCategory(next);
    if (next) track('readiness_assessment_further_reading_opened', { category: key });
  };

  const handleToggleReview = (row) => {
    const next = row.status !== 'reviewed';
    // Intent, paired with the server-side `..._saved` event the mutation emits —
    // the same split as retake_started / restarted.
    track('readiness_assessment_area_review_clicked', { category: row.key, reviewed: next });
    onToggleReview(row.key, next);
  };

  // The "all resources" panel is server-authored HTML, so its links can't carry
  // their own handlers — delegate from the wrapper instead.
  const handleAllResourcesClick = (event) => {
    const anchor = event.target.closest && event.target.closest('a');
    if (!anchor) return;
    track('readiness_assessment_further_reading_clicked', {
      category: 'all_resources',
      url: anchor.getAttribute('href'),
      placement: 'see_all',
    });
  };

  return (
    <section
      className={`bg-succession-bg-primary border border-succession-border rounded-lg p-6 shadow-succession-md hover:shadow-succession-lg transition-shadow ${className}`}
      aria-label="Succession readiness snapshot">
      <div className="flex flex-col sm:flex-row items-center gap-5 sm:gap-6">
        {/* Radial gauge */}
        <ReadinessRing rows={rows} overall={overall} />

        {/* Header + status */}
        <div className="flex-1 min-w-0 text-center sm:text-left">
          <div className="flex items-center justify-center sm:justify-start gap-2">
            <ShieldCheck className="w-5 h-5 text-succession-primary flex-shrink-0" />
            {/* `m-0` is a reset, not styling: application.css gives bare h3 a
                20px bottom margin, which inflated this flex row to 38px and left
                the 20px icon centring 9px below the title text. */}
            <h3 className="m-0 text-lg font-semibold text-succession-text-primary leading-none">Succession Readiness</h3>
          </div>
          <p className="text-sm text-succession-text-secondary mt-2">
            {statusLine}
            {takenLabel && (
              <span className="text-succession-text-secondary/70"> Assessed {takenLabel}.</span>
            )}
          </p>
          {/* The answer to what RJ actually asked — how does an area stop saying
              "To do"? Its own line rather than appended to the sentence above,
              which already carries the count and the assessed date. Hidden once
              nothing can move, so a fully resolved card stays quiet. */}
          {onToggleReview && rows.some((row) => row.status !== 'done') && (
            <p className="text-xs text-succession-text-secondary mt-1.5">
              Areas update as you complete the work here. Mark one reviewed if you handled it elsewhere.
            </p>
          )}
          {/* The "See your recommended steps" link is gone: once the assessment
              is complete the guide directly below is titled "Your Recommended
              Next Steps", so the link pointed at something already on screen. */}
          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-5 gap-y-1 mt-2">
            {onRetake && (
              <button
                type="button"
                onClick={onRetake}
                disabled={retaking}
                className="inline-flex items-center gap-1.5 text-sm text-succession-text-secondary hover:text-succession-text-primary transition-colors disabled:opacity-50 group"
                title="Answer the questions again to refresh your score">
                <RotateCcw className={`w-3.5 h-3.5 ${retaking ? 'animate-spin' : 'group-hover:-rotate-45 transition-transform'}`} />
                {retaking ? 'Reopening…' : 'Retake'}
              </button>
            )}
            {/* A plain anchor, not a fetch: the endpoint returns a file, so the
                browser download is the whole interaction. It also means the
                server-side `readiness_assessment_downloaded` event always fires,
                with no client duplicate to reconcile. */}
            {downloadUrl && (
              <a
                href={downloadUrl}
                className="inline-flex items-center gap-1.5 text-sm text-succession-text-secondary hover:text-succession-text-primary transition-colors"
                title="Save a PDF of these results, with your further reading links">
                <Download className="w-3.5 h-3.5" />
                Download
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Category breakdown.
          One type scale throughout: every line here is text-xs. Hierarchy comes
          from weight and colour, not size — an 11px control next to a 12px label
          reads as a mistake rather than a level. gap-y-5 so an expanded row
          doesn't crowd the one beneath it. */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
        {rows.map((row) => {
          const open = openCategory === row.key;
          const meta = STATUS_META[row.status] || STATUS_META.to_do;
          // Nothing left to attest once an area is objectively Done, so the
          // control is absent there rather than disabled — an advisor should
          // never be able to "review" their way over verified evidence.
          const canReview = Boolean(onToggleReview) && row.status !== 'done';
          return (
            <div key={row.key}>
              {/* Status word only — no percentage, no bar, no fraction.
                  Nearly every rollup is a single yes/no question (counted from
                  the seeded templates: 5 of 6 rows at 1-3 years, 4 of 5 at 4-6,
                  3 of 4 at 7+), so a percentage could only ever read 0 or 100
                  and the bar implied a continuum the scoring cannot produce.

                  `score`/`max` are still not surfaced, though the original
                  reason is gone. `max` used to mix answered questions with
                  server-side signals — catastrophic_plan read 2/2 for an advisor
                  with one in-scope question and a filed plan — so two rows
                  showing "n/2" could mean different things. That bonus point was
                  removed with the interstitial sentence that explained it, and
                  `max` is now purely a count of questions asked.

                  The first reason above still stands on its own: with nearly
                  every rollup a single question, a fraction reads 1/1 or 0/1 and
                  says nothing the status word doesn't. Surfacing it is now a
                  design choice rather than a correctness problem. A gap remains
                  score < max. */}
              <div className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-1.5 text-xs font-medium text-succession-text-primary min-w-0">
                  {row.status === 'to_do' && (
                    <span className="w-1.5 h-1.5 rounded-full bg-succession-warning flex-shrink-0" aria-hidden />
                  )}
                  {row.status === 'done' && (
                    <CheckCircle2 className="w-3.5 h-3.5 text-succession-success flex-shrink-0" aria-hidden />
                  )}
                  {row.status === 'reviewed' && (
                    <UserCheck className="w-3.5 h-3.5 text-succession-text-secondary flex-shrink-0" aria-hidden />
                  )}
                  <span className="truncate">{row.label}</span>
                </span>

                {/* Status and its control sit together on the right, because
                    that is where the eye already goes to read the state — the
                    label version of this control lived down beside "Further
                    reading" and read as a second disclosure. */}
                <span className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-xs font-semibold ${meta.tone}`}>{meta.label}</span>

                  {canReview && (
                    <button
                      type="button"
                      onClick={() => handleToggleReview(row)}
                      disabled={reviewingCategory === row.key}
                      // Icon-only, so the accessible name carries the whole
                      // meaning and `title` supplies the sighted tooltip.
                      aria-label={row.status === 'reviewed'
                        ? `Undo reviewed for ${row.label}`
                        : `Mark ${row.label} reviewed`}
                      title={row.status === 'reviewed'
                        ? 'Clear the reviewed mark for this area'
                        : 'Mark this area reviewed if you handled it elsewhere'}
                      className="p-1 -m-1 rounded text-succession-text-secondary hover:text-succession-primary transition-colors disabled:opacity-50">
                      {row.status === 'reviewed' ? (
                        <RotateCcw className="w-3.5 h-3.5" aria-hidden />
                      ) : (
                        // The same glyph Done uses, hollow and grey: a preview of
                        // the state one click away.
                        <CheckCircle2 className="w-3.5 h-3.5" aria-hidden />
                      )}
                    </button>
                  )}
                </span>
              </div>

              {/* Further reading — collapsed by default. A category RJ has
                  supplied no material for renders nothing here. */}
              {row.links.length > 0 && (
                <div className="mt-2.5">
                  <button
                    type="button"
                    onClick={() => toggleCategory(row.key)}
                    aria-expanded={open}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-succession-text-secondary hover:text-succession-primary transition-colors">
                    <ChevronDown
                      className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
                      aria-hidden
                    />
                    Further reading ({row.links.length})
                  </button>

                  {/* Indented to clear the chevron, so titles align with the
                      disclosure label rather than hanging under the icon. */}
                  {open && (
                    <ul className="mt-2.5 space-y-2.5 pl-5">
                      {row.links.map((link) => {
                        const external = isExternal(link.url);
                        return (
                          <li key={link.url}>
                            <a
                              href={link.url}
                              target={external ? '_blank' : undefined}
                              // Set unconditionally: a no-op on same-origin links,
                              // and react/jsx-no-target-blank can't verify a
                              // conditional rel against a conditional target.
                              rel="noopener noreferrer"
                              onClick={() => track('readiness_assessment_further_reading_clicked', {
                                category: row.key,
                                url: link.url,
                                placement: 'gap_row',
                              })}
                              // Underlined, but softly: a hard underline on every
                              // item in a list this dense fights the descriptions
                              // beneath it. Colour alone would be a weak
                              // affordance, so the rule stays and strengthens on
                              // hover instead of disappearing.
                              className="inline-flex items-baseline gap-1 text-xs font-medium text-succession-primary underline decoration-succession-primary/30 underline-offset-2 hover:decoration-succession-primary transition-colors">
                              {link.title}
                              {external && (
                                <ExternalLink className="w-3 h-3 flex-shrink-0 self-center" aria-hidden />
                              )}
                            </a>
                            {link.description && (
                              <div className="mt-0.5 text-xs text-succession-text-secondary leading-relaxed">
                                {link.description}
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Every resource in one place. Shown regardless of gaps — an advisor
          scoring 100% has no gap rows, so this is the only thing they'd see. */}
      {result.allResourcesHtml && (
        <div className="mt-6 pt-5 border-t border-succession-border">
          <button
            type="button"
            onClick={() => {
              const next = !showAllResources;
              setShowAllResources(next);
              if (next) track('readiness_assessment_all_resources_opened');
            }}
            aria-expanded={showAllResources}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-succession-text-secondary hover:text-succession-primary transition-colors">
            <ChevronDown
              className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${showAllResources ? 'rotate-180' : ''}`}
              aria-hidden
            />
            All succession resources
          </button>

          {showAllResources && (
            /* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
            <div onClick={handleAllResourcesClick}>
              <ReadinessHtmlBlocks blocks={[result.allResourcesHtml]} className="mt-3" />
            </div>
          )}
        </div>
      )}
    </section>
  );
};

ReadinessScoreCard.propTypes = {
  result: PropTypes.shape({
    categoryScores: PropTypes.object,
    categoryStatuses: PropTypes.arrayOf(PropTypes.shape({
      category: PropTypes.string,
      status: PropTypes.oneOf(['to_do', 'done', 'reviewed']),
      inScope: PropTypes.bool,
    })),
    overallScore: PropTypes.number,
    completedAt: PropTypes.string,
    furtherReading: PropTypes.arrayOf(PropTypes.shape({
      category: PropTypes.string,
      links: PropTypes.arrayOf(PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
        description: PropTypes.string,
      })),
    })),
    allResourcesHtml: PropTypes.string,
  }),
  // Omit to hide the retake affordance entirely.
  onRetake: PropTypes.func,
  retaking: PropTypes.bool,
  // Omit to render the breakdown read-only.
  onToggleReview: PropTypes.func,
  reviewingCategory: PropTypes.string,
  // Omit to hide the download affordance entirely.
  downloadUrl: PropTypes.string,
  className: PropTypes.string,
};

ReadinessScoreCard.defaultProps = {
  result: null,
  onRetake: undefined,
  retaking: false,
  onToggleReview: undefined,
  reviewingCategory: null,
  downloadUrl: undefined,
  className: '',
};

export default ReadinessScoreCard;
